import React from 'react'

import styled from '@emotion/styled'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { LockLockedFillIcon } from '@arcade/web/icons/react'
import { ArcadeFieldLabel, CardNumberInput, FieldLabel } from '@cash-design-system/react'
import { useArcadeMigrationFeatureFlag } from '@cash-web/shared-data-access-feature-flags'
import { isFirstDigitsAmex, isValidCardNumber } from '@cash-web/shared-util-validation'

import { FieldController } from '../FieldController/FieldController'
import { FormMaskedInput } from '../FormMaskedInput'

export type CardNumberType = 'DEBIT_CARD' | 'CREDIT_CARD'
export type CardNumberProps = {
  name: string
  type: CardNumberType
  autoFocus?: boolean
}
const CardNumberWrapper = styled.div`
  position: relative;
`

export const CardNumber: React.FC<CardNumberProps> = ({ name, autoFocus, type, ...rest }) => {
  const { t } = useTranslation()
  const { watch } = useFormContext()
  const watchCardNumber = watch(name)
  const isAmex = isFirstDigitsAmex(watchCardNumber)
  const isArcadeMigrationEnabled = useArcadeMigrationFeatureFlag('apps')

  const { label, placeholder, requiredText, invalidText } = (() => {
    switch (type) {
      case 'DEBIT_CARD':
        return {
          label: t('Debit card number', { context: 'label for debit card number form field' }),
          placeholder: t('0000 0000 0000 0000', { context: 'placeholder for debit card number form field' }),
          requiredText: t('Debit card number is required.', {
            context: 'error message for debit card number form field',
          }),
          invalidText: t('Invalid debit card number.', { context: 'error message for debit card number form field' }),
        }
      case 'CREDIT_CARD':
        return {
          label: t('Credit card number', { context: 'label for credit card number form field' }),
          placeholder: t('0000 0000 0000 0000', { context: 'placeholder for credit card number form field' }),
          requiredText: t('Enter a credit card number.', {
            context: 'error message for credit card number form field',
          }),
          invalidText: t('Invalid credit card number.', { context: 'error message for credit card number form field' }),
        }
    }
  })()

  if (isArcadeMigrationEnabled) {
    return (
      <CardNumberWrapper>
        <ArcadeFieldLabel label={label}>
          <FieldController
            FieldComponent={CardNumberInput}
            componentProps={{
              autoFocus,
              placeholder,
              cardType: type === 'DEBIT_CARD' ? 'debit' : 'credit',
              ...rest,
              value: watchCardNumber,
            }}
            name={name}
            rules={{
              required: {
                value: true,
                message: requiredText,
              },
              validate: (value: string) => isValidCardNumber(value) || invalidText,
            }}
          />
        </ArcadeFieldLabel>
      </CardNumberWrapper>
    )
  }

  return (
    <CardNumberWrapper>
      <FieldLabel label={label}>
        <FormMaskedInput
          name={name}
          placeholder={placeholder}
          autoComplete="cc-number"
          autoFocus={autoFocus}
          format={isAmex ? '#### ###### #####' : '#### #### #### ####'}
          rules={{
            required: {
              value: true,
              message: requiredText,
            },
            validate: value => isValidCardNumber(value) || invalidText,
          }}
          endAdornment={<LockLockedFillIcon size={24} data-testid="lock" />}
          {...rest}
        />
      </FieldLabel>
    </CardNumberWrapper>
  )
}
